import { useCallback, useEffect, useRef, useState } from 'react'
import Field, { FieldProps } from './Field'
import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css' // theme css file
import { DateRange, DateRangeProps, Range, RangeKeyDict } from 'react-date-range'
import moment from 'moment'
import { BaseProps } from './Element'

type DateRangeSelectorProps = FieldProps &
  DateRangeProps & {
    seperator: '&' | 'to'
    rangeSelected?: (range: Range) => void
    placeholder?: string
    disabled?: boolean
    autoApply?: boolean
    autoRangeDays?: number
  }

export function DateRangeSelector(props: DateRangeSelectorProps) {
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<Range>(
    props.ranges?.[0] ?? {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    }
  )

  const formatDateRange = useCallback(
    (range: Range) => {
      if (!range.startDate || !range.endDate) return ''
      return `${moment(range.startDate).format('DD MMM YYYY')} ${props.seperator} ${moment(
        range.endDate
      ).format('DD MMM YYYY')}`
    },
    [props.seperator]
  )

  const [value, setValue] = useState<string>(formatDateRange(range))
  const refCalendar = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (props.ranges?.[0]) {
      const newRange = props.ranges[0]
      setRange(newRange)
      setValue(formatDateRange(newRange))
    }
  }, [props.ranges, formatDateRange])

  const hideOnEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const hideOnClickOutside = (e: MouseEvent) => {
    if (refCalendar.current && !refCalendar.current.contains(e.target as Node)) {
      setOpen(false)
    }
  }

  const handleRangeChange = (rangesByKey: RangeKeyDict) => {
    const newRange = rangesByKey.selection
    if (!newRange || !newRange.startDate) {
      return
    }

    let updatedRange: Range = {
      startDate: newRange.startDate,
      endDate: newRange.endDate,
      key: 'selection',
    }

    if (props.autoRangeDays && props.autoRangeDays > 1) {
      const calculatedEnd = new Date(newRange.startDate)
      calculatedEnd.setDate(calculatedEnd.getDate() + props.autoRangeDays - 1)
      updatedRange = {
        startDate: newRange.startDate,
        endDate: calculatedEnd,
        key: 'selection',
      }
    }

    if (!updatedRange.endDate) {
      return
    }

    setRange(updatedRange)
    setValue(formatDateRange(updatedRange))

    if (props.autoApply && props.rangeSelected) {
      props.rangeSelected(updatedRange)
      setOpen(false)
    }
  }

  const applySelectedRange = () => {
    if (range.startDate && range.endDate) {
      const finalRange = {
        startDate: range.startDate,
        endDate: range.endDate,
        key: 'selection',
      }
      setValue(formatDateRange(finalRange))
      if (props.rangeSelected) {
        props.rangeSelected(finalRange)
      }
    }
    setOpen(false)
  }

  useEffect(() => {
    document.addEventListener('keydown', hideOnEscape, true)
    document.addEventListener('click', hideOnClickOutside, true)

    return () => {
      document.removeEventListener('keydown', hideOnEscape, true)
      document.removeEventListener('click', hideOnClickOutside, true)
    }
  }, [])

  return (
    <div className='position-relative' style={{ zIndex: open ? 1050 : undefined }}>
      <Field
        onClick={props.disabled ? undefined : () => setOpen(true)}
        title={props.title}
        readOnly={true}
        disabled={props.disabled}
        autoComplete='off'
        value={value}
        placeholder={props.placeholder}
      >
        <span
          className='position-absolute end-0 top-50 mt-1 me-3'
          style={{
            cursor: props.disabled ? 'default' : 'pointer',
          }}
          onClick={props.disabled ? undefined : () => setOpen(true)}
        >
          <i className='fa fa-calendar fs-4'></i>
        </span>
      </Field>
      <div
        ref={refCalendar}
        className='position-absolute d-flex flex-column'
        style={{
          top: '100%',
          left: 0,
          zIndex: 1,
          marginTop: 4,
        }}
      >
        {open && !props.disabled ? (
          <>
            <div className='bg-white rounded shadow-sm p-2'>
              <DateRange
                onChange={handleRangeChange}
                moveRangeOnFirstSelection={false}
                ranges={[{ ...range, key: 'selection' }]}
                weekStartsOn={1}
                months={1}
                direction='horizontal'
                preventSnapRefocus={true}
                calendarFocus='forwards'
              />
            </div>
            {!props.autoApply && (
              <button
                className='btn btn-primary mt-2'
                onClick={(e) => {
                  e.stopPropagation()
                  applySelectedRange()
                }}
              >
                Apply
              </button>
            )}
          </>
        ) : null}
      </div>
      {props.children}
    </div>
  )
}

type DateRangeInputProps = BaseProps & {
  name: string
  seperator: '&' | 'to'
  onChange: Function
  ranges?: Range[]
  placeholder?: string
  disabled?: boolean
  autoApply?: boolean
  autoRangeDays?: number
}
export function DateRangeInput(props: DateRangeInputProps) {
  const [range, setRange] = useState<Range>(
    props.ranges?.[0] ?? {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    }
  )

  // Keep internal state in sync with external changes to ranges (e.g., reset)
  useEffect(() => {
    if (props.ranges && props.ranges[0]) {
      setRange(props.ranges[0])
    } else {
      setRange({ startDate: undefined, endDate: undefined, key: 'selection' })
    }
  }, [props.ranges])
  return (
    <DateRangeSelector
      title={props.title}
      seperator={props.seperator}
      placeholder={props.placeholder}
      ranges={[range]}
      disabled={props.disabled}
      autoApply={props.autoApply}
      autoRangeDays={props.autoRangeDays}
      rangeSelected={(range: Range) => {
        setRange(range)
        if (props.onChange) {
          let event = {
            target: {
              name: props.name,
              value: `${moment(range.startDate).format('YYYY-MM-DD')} ${props.seperator} ${moment(
                range.endDate
              ).format('YYYY-MM-DD')}`,
            },
          }
          props.onChange(event)
        }
      }}
    />
  )
}
